import React, { useEffect, useState } from "react"
import { BiSolidEditAlt } from "react-icons/bi"
import { FaSpinner } from "react-icons/fa"

import Button from "@fuse/sharedComponents/buttons/SolidBtn"
import { showSuccessToast } from "@fuse/sharedComponents/themeToast/toastUtils"
import { useRecoilState } from "recoil"

import { userState } from "@fuse/recoil/signup"

import { auth } from "@firebaseConfig/clientApp"

import {
  getUserInfo,
  resendEmailVerificationAPI,
} from "@api/firebaseWrappedApis"

import { getWebmailUrl } from "@fuse/utils/emailUtils"

interface EmailVerificationPopupProps {
  isLoading: boolean
  isEmailInboxVerification: boolean
  setIsEmailInboxVerification?: (v: boolean) => void
  onClose: () => void
  onVerifyEmail: (v: string) => void
}

const EmailVerificationPopup: React.FC<EmailVerificationPopupProps> = ({
  isLoading,
  isEmailInboxVerification,
  setIsEmailInboxVerification,
  onClose,
  onVerifyEmail,
}) => {
  const [user, setUser] = useRecoilState(userState)
  const [email, setEmail] = useState<string>(user?.email || "")
  const [error, setError] = useState<string>("")
  const [isResendLoading, setIsResendLoading] = useState<boolean>(false)

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const handleSubmit = () => {
    if (!validateEmail(email)) {
      setError("Please enter a valid email address.")
      return
    }
    setError("")
    onVerifyEmail(email)
  }

  const handleOverlayClick = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  const onResendMail = () => {
    setIsResendLoading(true)
    resendEmailVerificationAPI({})
      .then(() => {
        showSuccessToast("Mail sent successfully!")
      })
      .finally(() => {
        setIsResendLoading(false)
      })
  }

  if (isEmailInboxVerification) {
    return (
      <div
        className="z-[200] fixed inset-0 flex items-center justify-center bg-black bg-opacity-50"
        onClick={handleOverlayClick}
      >
        <div className="w-[90%] max-w-lg bg-white/10 backdrop-blur-md rounded-2xl p-8 space-y-6 mx-4">
          {/* Title */}
          <div className="text-center">
            <h2 className="text-white text-2xl font-semibold">
              Verify your email
            </h2>

            <p className="text-white/70 mt-3 text-sm leading-relaxed">
              We sent an email to <br />
              <span className="flex items-center justify-center gap-2 mt-2 ">
                <span className="font-semibold text-white">{email}</span>
                <span className="p-1 bg-white/10 rounded cursor-pointer">
                  <BiSolidEditAlt
                    size={14}
                    color={"#fff"}
                    onClick={() => setIsEmailInboxVerification(false)}
                  />
                </span>
              </span>
              <br />
              <br />
              Just click on the button in that email to complete your
              verification.
            </p>
          </div>

          <div className="w-full flex items-center justify-center">
            <Button
              className="w-full bg-white/10 text-white py-3 rounded-full transition text-sm max-w-72 hover:bg-white/30 disabled:opacity-50"
              onClick={() => {
                const webmailUrl = getWebmailUrl(email)
                window.open(webmailUrl, "_blank")
              }}
              disabled={isResendLoading}
            >
              Open email app
            </Button>
          </div>

          <div className="text-center">
            <p className="text-white/70 text-sm">
              Haven't received email?{" "}
              <span
                className="text-white underline cursor-pointer hover:text-white/80"
                onClick={onResendMail}
              >
                {isResendLoading ? "Sending..." : "Resend"}
              </span>
            </p>
          </div>

          <p className="text-white/35 text-xs text-center leading-relaxed">
            Didn't receive email? Check your spam folder,
            <br />
            or try another email address
          </p>
        </div>
      </div>
    )
  }

  return (
    <div
      className="z-[200] fixed inset-0 flex items-center justify-center bg-black bg-opacity-50"
      onClick={handleOverlayClick}
    >
      <div className="w-[90%] max-w-lg bg-white/10 backdrop-blur-md rounded-2xl p-8 space-y-6 mx-4">
        {/* Title */}
        <div className="text-center">
          <h2 className="text-white text-2xl font-semibold">
            Secure your account
          </h2>

          <p className="text-white/70 mt-3 text-sm leading-relaxed text-left">
            Your email is necessary for us to stay connected and share updates
            straight to your inbox.
          </p>
        </div>

        {/* Email Input */}
        <div className="space-y-2">
          <div className="text-white text-sm font-medium text-left">Email</div>
          <div className="px-4 bg-white/10 rounded-lg flex items-center">
            <input
              type="email"
              autoFocus
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="e.g. john@gmail.com"
              className="w-full h-12 bg-transparent text-white text-[15px] placeholder-white/40 outline-none"
              disabled={isLoading}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  handleSubmit()
                }
              }}
            />
          </div>
          {error && <div className="text-red-400 text-sm px-1">{error}</div>}
        </div>

        {/* Verify Button */}
        <Button
          className="w-full bg-white/10 text-white py-3 rounded-full font-medium transition hover:bg-white/30 disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={handleSubmit}
          disabled={isLoading}
        >
          {isLoading ? (
            <div className="flex items-center justify-center">
              <FaSpinner className="animate-spin mr-2" size={16} />
              Verifying...
            </div>
          ) : (
            "Verify"
          )}
        </Button>
      </div>
    </div>
  )
}

export default EmailVerificationPopup
